<template>
    <form
        class="needs-validation"
        id="register-form"
        novalidate
        ref="contactForm"
        :class="validationErrorClass"
        @submit.prevent="submit"
    >
        <div class="d-flex row">
            <div class="col-md-6">
                <label for="first-name" class="form-label required">{{ 'common.form-first-name' | l }}</label>
                <input
                    required
                    type="text"
                    class="form-control"
                    id="first-name"
                    v-model="formData.firstName"
                    :class="{'filled': isFiled(formData.firstName)}"
                    :pattern="patterns.alphaDash"
                >
            </div>
            <div class="col-md-6">
                <label for="last-name" class="form-label required">{{ 'common.form-last-name' | l }}</label>
                <input
                    required
                    type="text"
                    class="form-control"
                    id="last-name"
                    v-model="formData.lastName"
                    :class="{'filled': isFiled(formData.lastName)}"
                    :pattern="patterns.alphaDash"
                >
            </div>
        </div>
        <div>
            <label for="email" class="form-label required">{{ 'common.form-email' | l }}</label>
            <input
                required
                type="text"
                @blur="checkEmail()"
                class="form-control"
                id="email"
                :pattern="patterns.email"
                v-model="formData.email"
                :class="{'filled': isFiled(formData.email), 'is-invalid': emailExists}"
            >
            <div v-if="emailExists" class="invalid-feedback">
                {{ 'validation.error.email-exists' | l }}
                <a :href="loginViewUrl" target="_blank">{{ 'validation.error.email-exists-login' | l }}</a>
            </div>
        </div>
        <div>
            <label for="phone-number" class="form-label">{{ 'common.form-phone-number' | l }}</label>
            <input
                type="number"
                class="form-control"
                id="phone-number"
                v-model="formData.phone"
                :class="{'filled': isFiled(formData.phone)}"
            >
        </div>
        <div class="position-relative">
            <label for="password" class="form-label required">{{ 'common.form-create-password' | l }}</label>
            <input
                required
                :type="getPasswordFieldType()"
                class="form-control"
                id="password"
                v-model="formData.password"
                :class="{'filled': isFiled(formData.password), 'is-invalid': !passwordValid || !passwordConfirmed}"
            >
            <img
                class="eye"
                src="/img/far-fa-eye.svg"
                alt="eye"
                @mousedown="changePasswordFieldType(false)"
                @mouseup="changePasswordFieldType(true)"
            />
            <span>{{ 'common.form-create-password-description' | l }}</span>
            <div v-if="!passwordValid" class="invalid-feedback">{{ 'common.form-create-password-description' | l }}</div>
        </div>
        <div>
            <label for="repeat-password" class="form-label required">{{ 'common.form-repeat-password' | l }}</label>
            <input
                required
                :type="getPasswordFieldType()"
                class="form-control"
                id="repeat-password"
                v-model="formData.repeatPassword"
                :class="{'filled': isFiled(formData.repeatPassword), 'is-invalid': !passwordValid || !passwordConfirmed}"
            >
            <div v-if="!passwordConfirmed" class="invalid-feedback">{{ 'validation.error.repeat-password' | l }}</div>
        </div>
        <div class="form-check d-flex align-items-center">
            <input
                type="checkbox"
                class="form-check-input me-2"
                id="accept-privacy"
                v-model="formData.policyAccepted"

            >
            <label for="accept-privacy" class="form-check-label label-privacy-text">{{ 'common.form-accept-privacy' | l }}</label>
        </div>
        <button
            type="submit"
            class="btn btn-primary w-100 btn-register mt-5"
            :class="{'disabled': !formData.policyAccepted}"
        >
            {{ 'common.register' | l }}
        </button>
    </form>
</template>

<script>
    import Repository from '../repositories/Repository';

    export default {
        name: 'RegisterForm',
        props: {
            submitUrl: {
                type: String,
                default: ''
            },
            checkEmailUrl: {
                type: String,
                default: ''
            },
            loginViewUrl: {
                type: String,
                default: ''
            },
            userData: {
                type: Object,
                default: function () {
                    return {
                        firstName: null,
                        lastName: null,
                        email: null,
                        phone: null,
                        password: null,
                        repeatPassword: null,
                        invitationToken: null,
                        policyAccepted: false
                    }
                }
            },
        },
        data() {
            return {
                formData: this.userData,
                passwordVisible: false,
                validationErrorClass: '',
                emailExists: false,
                passwordValid: true,
                passwordConfirmed: true,
                patterns: window.patterns
            }
        },
        mounted() {

        },
        methods: {
            async submit() {
                this.isSubmitting = true;
                let valid = await this.isValid();
                if (!valid) {
                    this.isSubmitting = false;
                    return;
                }

                await this.$recaptchaLoaded();
                this.formData.recaptchaToken = await this.$recaptcha('register');

                let resp = await Repository.post(this.submitUrl, this.formData);
                if (resp && resp.success && resp.to) {
                    redirect(resp.to);
                }
            },
            async isValid() {
                this.validationErrorClass = 'was-validated';

                let isFormValid = await this.$refs.contactForm.checkValidity();
                let passwordValid = await this.checkPassword();
                let emailValid = await this.checkEmail();

                return isFormValid && passwordValid && emailValid;
            },
            isFiled(model) {
                if (!model) {
                    model = '';
                }

                return model.trim().length;
            },
            getPasswordFieldType() {
                return this.passwordVisible ? 'text' : 'password';
            },
            changePasswordFieldType(isPassword = true) {
                this.passwordVisible = !isPassword;
            },
            checkPassword() {
                this.passwordValid = true;
                this.passwordConfirmed = true;

                let password = this.formData.password;
                let passwordConfirmed = this.formData.repeatPassword === password;
                if (!passwordConfirmed) {
                    this.passwordConfirmed = false;
                }
                let check = false;
                if (password === null) {
                    password = '';
                }

                password = password.trim();
                if (password.length < 8) {
                    check = false;
                } else {
                    let strongPassword = new RegExp(this.patterns.strongPassword);
                    check = strongPassword.test(password);
                }

                this.passwordValid = check;
                return check;
            },
            async checkEmail() {
                this.emailExists = false;

                let email = this.formData.email;
                if (email === null) {
                    email = '';
                }
                email = email.trim();

                let emailRegex = new RegExp(this.patterns.email);
                if (!emailRegex.test(email)) {
                    return false;
                }
                let submitData = {
                    email: email,
                    invitationToken: this.formData.invitationToken
                };
                let data = await Repository.post(this.checkEmailUrl, submitData);

                let emailExists = true;
                if (data.data && typeof data.data.emailExists !== 'undefined') {
                    emailExists = data.data.emailExists;
                }

                this.emailExists = emailExists;

                return !emailExists;

            }
        }
    }
</script>
